import React, { useEffect, useRef, useState } from 'react';
import './select-dropdown.scss';

const defaultGetOptionLabel = (option) => option?.label ?? option?.title ?? String(option?.value ?? '');
const defaultGetOptionValue = (option) => option?.value ?? option?.id ?? option;

/**
 * General-purpose single-select dropdown.
 * @param {string} label - Label above the field (e.g. "Select a template")
 * @param {string} placeholder - Placeholder when nothing selected (e.g. "Required")
 * @param {Array<{value, label}>} options - List of options
 * @param {object} value - Selected option (controlled) or null
 * @param {function} onChange - (option) => void
 * @param {boolean} disabled - Disable the trigger
 * @param {function} getOptionLabel - (option) => string
 * @param {function} getOptionValue - (option) => any
 */
export const SelectDropdown = ({
  label,
  placeholder,
  options = [],
  value = null,
  onChange,
  disabled = false,
  getOptionLabel = defaultGetOptionLabel,
  getOptionValue = defaultGetOptionValue
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedLabel = value != null ? getOptionLabel(value) : null;
  const isSelected = (option) =>
    value != null && getOptionValue(option) === getOptionValue(value);

  const handleSelect = (option) => {
    onChange?.(option);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="select-dropdown">
      {label && <div className="select-dropdown__label">{label}</div>}
      <button
        type="button"
        className="select-dropdown__trigger"
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        disabled={disabled}
      >
        <span className={selectedLabel ? 'select-dropdown__value' : 'select-dropdown__placeholder'}>
          {selectedLabel ?? placeholder}
        </span>
        <span className="select-dropdown__chevron" aria-hidden>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="select-dropdown__menu">
          <ul className="select-dropdown__options">
            {options.map((option) => (
              <li
                key={getOptionValue(option)}
                className={`select-dropdown__option ${isSelected(option) ? 'select-dropdown__option--selected' : ''}`}
                onClick={() => handleSelect(option)}
              >
                {getOptionLabel(option)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SelectDropdown;
